/**
 * Data grid (ADR-0010 P4.3) — browse + edit rows of one Data Table.
 *
 * Reads through the structured Data API (`appsApi.queryRows`, paginated)
 * and renders the table's **user** columns plus row actions. Add / edit
 * open a per-column form; values are coerced to the column's data type
 * on submit (boolean→bool, integer/numeric→number, json→parsed, empty→
 * null when the column is nullable). Delete is confirm-gated.
 *
 * The row `id` (a system stamp column, D16) is the update/delete key —
 * it's always present in the returned row dicts even though it's not a
 * user column shown for editing.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Braces,
  CalendarDays,
  ChevronDown,
  Download,
  EyeOff,
  Hash,
  Link2,
  List,
  type LucideIcon,
  Paperclip,
  Pencil,
  Plus,
  Rows3,
  Rows4,
  ToggleLeft,
  Trash2,
  Type,
  User,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { pageSelectionState, togglePage, toggleSelected } from "@/lib/gridSelection";
import { EmptyState } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { usePaginationState } from "@/lib/pagination";
import { appsApi, type AppEnv, type ColumnOut, type FileValue, type Row } from "@/lib/api-apps";
import { columnTypeLabel, isComputedColumn } from "@/lib/columnPayload";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { Table2 } from "lucide-react";


/** Narrow a raw cell value to a `file`-column descriptor, or null. */
function asFileValue(v: unknown): FileValue | null {
  if (v && typeof v === "object" && typeof (v as FileValue).ref === "string") {
    return v as FileValue;
  }
  return null;
}

/** Read-only `file`-column cell: a chip that downloads the attachment
 *  (authenticated fetch → client-side save). */
export function FileCell({ appId, value }: { appId: string; value: unknown }) {
  const f = asFileValue(value);
  if (!f) return <span className="text-muted-foreground">—</span>;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-0.5 text-xs hover:bg-elevated"
      onClick={() => appsApi.downloadAttachment(appId, f.ref, f.filename).catch(() => {})}
    >
      <Paperclip className="h-3 w-3" strokeWidth={1.5} />
      <span className="max-w-[12rem] truncate">{f.filename || f.ref}</span>
      <Download className="h-3 w-3" strokeWidth={1.5} />
    </button>
  );
}

/** Editor control for a `file` column: upload (form-less) → descriptor, with
 *  the current attachment shown as a removable chip. */
function FileField({
  appId,
  value,
  onChange,
}: {
  appId: string;
  value: FileValue | null;
  onChange: (v: FileValue | null) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const upload = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      onChange(await appsApi.uploadAppAttachment(appId, file));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded border border-border bg-surface px-2 py-0.5 text-xs">
          <Paperclip className="h-3 w-3" strokeWidth={1.5} />
          <span className="max-w-[12rem] truncate">{value.filename || value.ref}</span>
        </span>
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          {t("common.remove", "Remove")}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <input
        type="file"
        disabled={busy}
        className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-2 file:py-1 file:text-sm"
        onChange={(e) => upload(e.target.files?.[0] ?? null)}
      />
      {err && <p className="text-xs text-danger">{err}</p>}
    </div>
  );
}


/** The app's app_user members (id → display label), shared across member
 *  cells/fields via the react-query cache (ADR-0020 P3b). */
function useAppMembers(appId: string) {
  return useQuery({
    queryKey: ["apps", appId, "members"],
    queryFn: () => appsApi.listMembers(appId),
    select: (ms) =>
      ms
        .filter((m) => m.principal_type === "app_user")
        .map((m) => ({ id: m.principal_id, label: m.principal_label || m.principal_id })),
  });
}

/** Read-only member cell: resolves the stored app_user id to a display name. */
export function MemberCell({ appId, value }: { appId: string; value: unknown }) {
  const { data } = useAppMembers(appId);
  if (value == null || value === "") return <>—</>;
  const m = (data ?? []).find((x) => x.id === String(value));
  return <>{m?.label ?? String(value)}</>;
}

/** Member (assignee) picker for the add/edit dialog — a native select (admin
 *  console) of the app's members. Stores the chosen app_user id. */
function MemberField({
  appId,
  value,
  required,
  onChange,
}: {
  appId: string;
  value: string;
  required?: boolean;
  onChange: (id: string) => void;
}) {
  const { data } = useAppMembers(appId);
  return (
    <select
      className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{required ? "Assegna a…" : "— non assegnato"}</option>
      {(data ?? []).map((m) => (
        <option key={m.id} value={m.id}>
          {m.label}
        </option>
      ))}
    </select>
  );
}


/** Relation picker: a searchable dropdown of the target table's rows
 *  (`appsApi.tableOptions`), storing the chosen row id while showing its
 *  label. Clearing sets the value to empty. */
function RelationField({
  appId,
  targetId,
  value,
  initialLabel,
  onChange,
}: {
  appId: string;
  targetId: string | null;
  value: string;
  initialLabel: string | null;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [label, setLabel] = React.useState<string | null>(initialLabel);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);
  const optsQ = useQuery({
    queryKey: ["apps", appId, "tableOptions", targetId, search],
    queryFn: () => appsApi.tableOptions(appId, targetId!, search || undefined),
    enabled: open && !!targetId,
  });
  if (!targetId) return <p className="text-xs text-danger">Relation has no target table.</p>;
  return (
    <div className="relative" ref={wrapRef}>
      <div
        className="flex w-full items-center justify-between rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
      >
        <button type="button" className="flex-1 text-left" onClick={() => setOpen((o) => !o)}>
          <span className={value ? "" : "text-muted-foreground"}>
            {label || (value ? value : "Select…")}
          </span>
        </button>
        {value && (
          <button
            type="button"
            className="ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              onChange("");
              setLabel(null);
            }}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm"
          />
          <div className="max-h-48 overflow-y-auto">
            {(optsQ.data ?? []).map((o) => (
              <button
                key={o.id}
                type="button"
                className="block w-full px-2 py-1.5 text-left text-sm hover:bg-elevated"
                onClick={() => {
                  onChange(o.id);
                  setLabel(o.label);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {o.label}
              </button>
            ))}
            {optsQ.data && optsQ.data.length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/** Multi relation picker (D20): the value is an ARRAY of target row ids; the
 *  chosen rows show as removable chips. `initialLabels` seeds the chip text
 *  from the `__rel` sidecar; a newly-picked row's label is remembered locally.
 *  Adding the same id twice is a no-op (it's a set). */
function RelationManyField({
  appId,
  targetId,
  value,
  initialLabels,
  onChange,
}: {
  appId: string;
  targetId: string | null;
  value: string[];
  initialLabels: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const wrapRef = React.useRef<HTMLDivElement>(null);
  useOutsideClick(wrapRef, () => setOpen(false), open);
  // id → label cache, seeded from the parallel `__rel` list.
  const [labels, setLabels] = React.useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    value.forEach((id, i) => {
      if (initialLabels[i]) m[id] = initialLabels[i];
    });
    return m;
  });
  const optsQ = useQuery({
    queryKey: ["apps", appId, "tableOptions", targetId, search],
    queryFn: () => appsApi.tableOptions(appId, targetId!, search || undefined),
    enabled: open && !!targetId,
  });
  if (!targetId) return <p className="text-xs text-danger">Relation has no target table.</p>;
  const add = (id: string, label: string) => {
    setLabels((m) => ({ ...m, [id]: label }));
    if (!value.includes(id)) onChange([...value, id]);
  };
  const remove = (id: string) => onChange(value.filter((v) => v !== id));
  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex w-full flex-wrap items-center gap-1 rounded-md border border-border bg-surface px-2 py-1.5 text-sm">
        {value.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded bg-elevated px-1.5 py-0.5 text-xs"
          >
            {labels[id] ?? id}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => remove(id)}
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((o) => !o)}
        >
          {value.length ? "+ add" : "Select…"}
        </button>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm"
          />
          <div className="max-h-48 overflow-y-auto">
            {(optsQ.data ?? [])
              .filter((o) => !value.includes(o.id))
              .map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="block w-full px-2 py-1.5 text-left text-sm hover:bg-elevated"
                  onClick={() => {
                    add(o.id, o.label);
                    setSearch("");
                  }}
                >
                  {o.label}
                </button>
              ))}
            {optsQ.data && optsQ.data.filter((o) => !value.includes(o.id)).length === 0 && (
              <p className="px-2 py-2 text-xs text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/** Classify a backend data_type string into the input affordance to
 *  render. Kept liberal (substring match) so the catalog can grow new
 *  aliases without breaking the grid. */
export type FieldKind =
  | "boolean" | "number" | "json" | "text" | "file" | "select" | "member"
  | "relation" | "relation_many" | "date" | "datetime";

/** Per-column affordance: a relation (kind) wins over its uuid data_type. */
export function fieldKindForColumn(c: ColumnOut): FieldKind {
  if (c.kind === "relation") return "relation";
  if (c.kind === "relation_many") return "relation_many";
  return fieldKindFor(c.data_type);
}

/** The target table id of a relation column (else null). */
export function columnTarget(c: ColumnOut): string | null {
  const t = (c.config as { target_table?: unknown })?.target_table;
  return typeof t === "string" ? t : null;
}

/** The resolved relation label for a row cell (server `__rel` sidecar), if any. */
export function relCellLabel(row: Row, name: string): string | null {
  const rel = (row as { __rel?: Record<string, string> }).__rel;
  return rel && typeof rel[name] === "string" ? rel[name] : null;
}

/** The parallel label list for a `relation_many` cell — `__rel[name]` is an
 *  array of labels matching the array of ids (D20). Empty when unresolved. */
export function relCellLabelsMany(row: Row, name: string): string[] {
  const rel = (row as { __rel?: Record<string, unknown> }).__rel;
  const v = rel?.[name];
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

/** The id list stored in a `relation_many` cell. */
export function relCellIds(row: Row, name: string): string[] {
  const v = row[name];
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

export function fieldKindFor(dataType: string | null | undefined): FieldKind {
  const t = (dataType ?? "").toLowerCase();
  if (t === "boolean" || t === "bool") return "boolean";
  if (t === "file") return "file";
  if (t === "member") return "member";
  if (t === "select") return "select";
  // A `multiselect` cell is a JSONB array — use the JSON affordance in the
  // admin grid (read-only cell + a valid JSON editor in the row dialog), so an
  // inline text edit can't corrupt the array. The PORTAL renders the proper
  // chip multi-picker; the operator authors `area` via the portal gestione.
  if (t === "multiselect") return "json";
  if (t === "date") return "date";
  if (t.includes("timestamp")) return "datetime";
  if (t.includes("json")) return "json";
  if (
    t.includes("int") ||
    t.includes("numeric") ||
    t.includes("decimal") ||
    t.includes("double") ||
    t.includes("real") ||
    t.includes("float")
  ) {
    return "number";
  }
  return "text";
}

/** The configured choices for a `select` column (its `config.options`). */
export function columnOptions(c: ColumnOut): string[] {
  const raw = (c.config as { options?: unknown })?.options;
  return Array.isArray(raw) ? raw.map((o) => String(o)) : [];
}

/** Render a raw row value for the read-only grid cell. */
export function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  // A `multiselect` cell is a JSON array → a readable comma-joined string.
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}


/** Airtable-style schema callbacks. When `DataGrid` receives this prop it turns
 *  the column headers into interactive field menus (rename / hide / delete) and
 *  the toolbar grows an "add field" button — the grid becomes the schema editor,
 *  not just a viewer. Absent → the grid is a pure read/edit surface (unchanged),
 *  so non-admin / portal call sites are untouched. */
export interface GridSchemaMenu {
  onRename: (col: ColumnOut) => void;
  onHide: (colId: string) => void;
  onDrop: (col: ColumnOut) => void;
  onAddField: () => void;
}

/** A small type glyph per field kind, so a column reads at a glance (à la
 *  Airtable's typed field headers). */
export function fieldKindIcon(kind: FieldKind): LucideIcon {
  switch (kind) {
    case "number":
      return Hash;
    case "boolean":
      return ToggleLeft;
    case "date":
    case "datetime":
      return CalendarDays;
    case "select":
      return List;
    case "member":
      return User;
    case "file":
      return Paperclip;
    case "relation":
    case "relation_many":
      return Link2;
    case "json":
      return Braces;
    default:
      return Type;
  }
}

/** One interactive column header: type glyph + name + a chevron menu (rename /
 *  hide / delete). Computed columns can't be renamed/dropped through the grid,
 *  so their menu only offers "hide". */
export function ColumnHeader({ col, schema }: { col: ColumnOut; schema: GridSchemaMenu }) {
  const { t } = useTranslation();
  const Icon = fieldKindIcon(fieldKindForColumn(col));
  const computed = isComputedColumn(col);
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
      <span className="truncate">{col.name}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("apps.fieldMenu", "Field menu")}
            className="ml-auto rounded p-0.5 text-muted-foreground/70 hover:bg-muted/60 hover:text-foreground"
          >
            <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <div className="px-2 py-1 text-[11px] text-muted-foreground">{columnTypeLabel(col)}</div>
          <DropdownMenuSeparator />
          {!computed && (
            <DropdownMenuItem onClick={() => schema.onRename(col)}>
              <Pencil className="h-4 w-4" strokeWidth={1.5} /> {t("apps.renameField", "Rename")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => schema.onHide(col.id)}>
            <EyeOff className="h-4 w-4" strokeWidth={1.5} /> {t("apps.hideField", "Hide")}
          </DropdownMenuItem>
          {!computed && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-danger" onClick={() => schema.onDrop(col)}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete", "Delete")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function DataGrid({
  appId,
  tableId,
  columns,
  env = "dev",
  schema,
}: {
  appId: string;
  tableId: string;
  columns: ColumnOut[];
  /** ADR-0010 P5.5 — which data environment to browse. The env is part of
   *  the query key so dev/prod caches never collide. */
  env?: AppEnv;
  /** Airtable-style schema editing affordances (admin only). When present the
   *  headers become field menus + the toolbar gains "add field". */
  schema?: GridSchemaMenu;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { page, pageSize, setPage, setPageSize } = usePaginationState();

  const [editing, setEditing] = React.useState<{ open: boolean; row: Row | null }>({
    open: false,
    row: null,
  });
  // Data grid 2a — multi-row selection + density.
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [dense, setDense] = React.useState(false);

  const rowsQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId, "rows", env, page, pageSize],
    queryFn: () => appsApi.queryRows(appId, tableId, { page, page_size: pageSize }, env),
  });

  const del = useMutation({
    mutationFn: (rowId: string) => appsApi.deleteRow(appId, tableId, rowId, env),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "rows"] }),
  });

  // Bulk delete: fire the existing per-row endpoint sequentially (each honours
  // the row's delete policy), then refresh + clear the selection.
  const bulkDel = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await appsApi.deleteRow(appId, tableId, id, env);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "rows"] });
      setSelected(new Set());
    },
  });

  const rows = rowsQ.data?.rows ?? [];
  const total = rowsQ.data?.total ?? 0;
  const pageIds = rows.map((r) => String(r.id));
  const headState = pageSelectionState(pageIds, selected);
  const cellPad: React.CSSProperties = { paddingTop: dense ? 4 : 8, paddingBottom: dense ? 4 : 8 };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">{t("apps.data", "Data")}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            aria-label={dense ? t("apps.comfortable", "Comfortable") : t("apps.compact", "Compact")}
            onClick={() => setDense((d) => !d)}
          >
            {dense ? <Rows4 className="h-4 w-4" strokeWidth={1.5} /> : <Rows3 className="h-4 w-4" strokeWidth={1.5} />}
            {dense ? t("apps.comfortable", "Comfortable") : t("apps.compact", "Compact")}
          </Button>
          {schema && (
            <Button variant="ghost" size="sm" onClick={schema.onAddField}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.addField", "Field")}
            </Button>
          )}
          <Button size="sm" onClick={() => setEditing({ open: true, row: null })}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.addRow", "Add row")}
          </Button>
        </div>
      </div>

      {/* Data grid 2a — bulk action bar (visible once rows are selected). */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
          <span className="font-medium">
            {t("apps.nSelected", "{{n}} selected", { n: selected.size })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger"
            disabled={bulkDel.isPending}
            onClick={() => {
              const ids = [...selected];
              if (confirm(t("apps.confirmBulkDelete", "Delete {{n}} selected rows?", { n: ids.length }))) {
                bulkDel.mutate(ids);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            {bulkDel.isPending ? t("apps.deleting", "Deleting…") : t("apps.deleteSelected", "Delete selected")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {t("common.clear", "Clear")}
          </Button>
        </div>
      )}

      {rows.length === 0 && !rowsQ.isLoading ? (
        <EmptyState
          icon={Table2}
          title={t("apps.noRows", "No rows yet")}
          description={t("apps.noRowsDescription", "Add a row, or write to this table from a workflow.")}
        />
      ) : (
        <>
          <div className="overflow-auto rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10">
                    <input
                      type="checkbox"
                      aria-label={t("apps.selectAll", "Select all on this page")}
                      checked={headState === "all"}
                      ref={(el) => {
                        if (el) el.indeterminate = headState === "some";
                      }}
                      onChange={() => setSelected((s) => togglePage(pageIds, s))}
                    />
                  </Th>
                  {columns.map((c) => (
                    <Th key={c.id}>
                      {schema ? <ColumnHeader col={c} schema={schema} /> : c.name}
                    </Th>
                  ))}
                  <Th className="w-20 text-right">{t("common.actions", "Actions")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, i) => {
                  const rid = String(row.id ?? i);
                  return (
                  <Tr key={rid} className={selected.has(rid) ? "bg-accent/5" : undefined}>
                    <Td style={cellPad}>
                      <input
                        type="checkbox"
                        aria-label={t("apps.selectRow", "Select row")}
                        checked={selected.has(rid)}
                        onChange={() => setSelected((s) => toggleSelected(s, rid))}
                      />
                    </Td>
                    {columns.map((c) => (
                      <Td key={c.id} className="max-w-[20rem] truncate text-sm" style={cellPad}>
                        {fieldKindForColumn(c) === "file" ? (
                          <FileCell appId={appId} value={row[c.name]} />
                        ) : fieldKindForColumn(c) === "member" ? (
                          <MemberCell appId={appId} value={row[c.name]} />
                        ) : fieldKindForColumn(c) === "relation" ? (
                          relCellLabel(row, c.name) ?? formatCell(row[c.name])
                        ) : fieldKindForColumn(c) === "relation_many" ? (
                          relCellLabelsMany(row, c.name).join(", ") ||
                          (relCellIds(row, c.name).length
                            ? `${relCellIds(row, c.name).length} linked`
                            : "—")
                        ) : (
                          formatCell(row[c.name])
                        )}
                      </Td>
                    ))}
                    <Td className="text-right" style={cellPad}>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.edit", "Edit")}
                          onClick={() => setEditing({ open: true, row })}
                        >
                          <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t("common.delete", "Delete")}
                          onClick={() => {
                            const id = row.id;
                            if (id != null && confirm(t("apps.confirmDeleteRow", "Delete this row?"))) {
                              del.mutate(String(id));
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <RowDialog
        appId={appId}
        tableId={tableId}
        columns={columns}
        env={env}
        open={editing.open}
        row={editing.row}
        onOpenChange={(open) => setEditing((e) => ({ ...e, open }))}
      />
    </section>
  );
}


/** Coerce a draft string/bool value to the JSON type for its column.
 *  Returns ``undefined`` to signal "drop this field" (empty + optional). */
export function coerceValue(
  kind: FieldKind,
  raw: string | boolean,
  required: boolean,
): unknown {
  if (kind === "boolean") return Boolean(raw);
  const s = typeof raw === "string" ? raw : String(raw);
  if (s.trim() === "") return required ? "" : null;
  if (kind === "number") {
    const n = Number(s);
    return Number.isNaN(n) ? s : n;
  }
  if (kind === "json") {
    try {
      return JSON.parse(s);
    } catch {
      return s;
    }
  }
  return s;
}


export function RowDialog({
  appId,
  tableId,
  columns,
  env = "dev",
  open,
  row,
  onOpenChange,
}: {
  appId: string;
  tableId: string;
  columns: ColumnOut[];
  env?: AppEnv;
  open: boolean;
  row: Row | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isEdit = row != null;

  // Draft holds string|boolean per column name (a `file` column holds the
  // attachment descriptor object instead); seed from the row on edit.
  type DraftVal = string | boolean | FileValue | string[] | null;
  const [draft, setDraft] = React.useState<Record<string, DraftVal>>({});

  React.useEffect(() => {
    if (!open) return;
    const seed: Record<string, DraftVal> = {};
    for (const c of columns) {
      const kind = fieldKindForColumn(c);
      const v = row?.[c.name];
      if (kind === "file") seed[c.name] = asFileValue(v); // descriptor or null
      else if (kind === "relation_many") seed[c.name] = Array.isArray(v) ? v.map(String) : [];
      else if (kind === "boolean") seed[c.name] = Boolean(v);
      else if (v === null || v === undefined) seed[c.name] = "";
      else if (typeof v === "object") seed[c.name] = JSON.stringify(v);
      else seed[c.name] = String(v);
    }
    setDraft(seed);
  }, [open, row, columns]);

  const save = useMutation({
    mutationFn: (data: Row) =>
      isEdit
        ? appsApi.updateRow(appId, tableId, String(row!.id), data, env)
        : appsApi.createRow(appId, tableId, data, env),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "rows"] });
      onOpenChange(false);
    },
  });

  const buildPayload = (): Row => {
    const out: Row = {};
    for (const c of columns) {
      // Computed columns (lookup/rollup/formula) have no physical column — the
      // backend rejects them (`apps_row:unknown_column`). They're already hidden
      // from the form; drop them from the payload too.
      if (isComputedColumn(c)) continue;
      const kind = fieldKindForColumn(c);
      if (kind === "file") {
        // The draft already holds the descriptor object (or null) — emit as-is.
        const fv = draft[c.name];
        if (fv != null || c.required) out[c.name] = (fv as FileValue) ?? null;
        continue;
      }
      if (kind === "relation_many") {
        // Draft holds the id array — emit it verbatim (replaces the set).
        out[c.name] = (draft[c.name] as string[]) ?? [];
        continue;
      }
      const raw = draft[c.name];
      const v = coerceValue(kind, (raw as string | boolean) ?? "", c.required);
      if (v !== undefined) out[c.name] = v;
    }
    return out;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t("apps.editRow", "Edit row") : t("apps.addRow", "Add row")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {columns.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t("apps.noColumnsForRow", "Add columns first to store data.")}
            </p>
          )}
          {/* Computed columns are read-only (server rejects writes) — never
              offer them as editable inputs in the add/edit dialog (P1.S4). */}
          {columns.filter((c) => !isComputedColumn(c)).map((c) => {
            const kind = fieldKindForColumn(c);
            const value = draft[c.name];
            return (
              <div key={c.id} className="space-y-1.5">
                <Label htmlFor={`row-${c.id}`}>
                  {c.name}
                  {c.required && <span className="ml-1 text-danger">*</span>}
                  <span className="ml-2 font-mono text-[11px] text-muted-foreground">{columnTypeLabel(c)}</span>
                </Label>
                {kind === "boolean" ? (
                  <Switch
                    id={`row-${c.id}`}
                    checked={Boolean(value)}
                    onCheckedChange={(v) => setDraft((d) => ({ ...d, [c.name]: v }))}
                  />
                ) : kind === "file" ? (
                  <FileField
                    appId={appId}
                    value={asFileValue(value)}
                    onChange={(fv) => setDraft((d) => ({ ...d, [c.name]: fv }))}
                  />
                ) : kind === "member" ? (
                  <MemberField
                    appId={appId}
                    value={typeof value === "string" ? value : ""}
                    required={c.required}
                    onChange={(id) => setDraft((d) => ({ ...d, [c.name]: id }))}
                  />
                ) : kind === "relation" ? (
                  <RelationField
                    appId={appId}
                    targetId={columnTarget(c)}
                    value={typeof value === "string" ? value : ""}
                    initialLabel={row ? relCellLabel(row, c.name) : null}
                    onChange={(id) => setDraft((d) => ({ ...d, [c.name]: id }))}
                  />
                ) : kind === "relation_many" ? (
                  <RelationManyField
                    appId={appId}
                    targetId={columnTarget(c)}
                    value={Array.isArray(value) ? value : []}
                    initialLabels={row ? relCellLabelsMany(row, c.name) : []}
                    onChange={(ids) => setDraft((d) => ({ ...d, [c.name]: ids }))}
                  />
                ) : kind === "select" ? (
                  <select
                    id={`row-${c.id}`}
                    className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [c.name]: e.target.value }))}
                  >
                    <option value="">{c.required ? "Select…" : "—"}</option>
                    {columnOptions(c).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : kind === "date" || kind === "datetime" ? (
                  <Input
                    id={`row-${c.id}`}
                    type={kind === "date" ? "date" : "datetime-local"}
                    // A stored ISO timestamp ("…T14:30:00+00:00") must be sliced
                    // to the input's expected shape (date: 10, datetime: 16).
                    value={
                      typeof value === "string" ? value.slice(0, kind === "date" ? 10 : 16) : ""
                    }
                    onChange={(e) => setDraft((d) => ({ ...d, [c.name]: e.target.value }))}
                  />
                ) : (
                  <Input
                    id={`row-${c.id}`}
                    type={kind === "number" ? "number" : "text"}
                    value={typeof value === "object" || typeof value === "boolean" ? "" : (value ?? "")}
                    onChange={(e) => setDraft((d) => ({ ...d, [c.name]: e.target.value }))}
                  />
                )}
              </div>
            );
          })}
          {save.isError && (
            <Alert tone="danger" title={t("apps.saveRowFailed", "Could not save row")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={save.isPending || columns.length === 0} onClick={() => save.mutate(buildPayload())}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
